import React, { useRef, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../i18n';
import { ArrowRight, Bot, Calendar, CheckCircle2, ClipboardList, Clock3, Languages, MessageCircle, Send, ShieldCheck, Stethoscope, TriangleAlert } from 'lucide-react';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
type ChatMessage = {
  id: number;
  role: 'assistant' | 'patient';
  text: string;
};
type LocalizedContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  startChat: string;
  whatsapp: string;
  book: string;
  trustedLabel: string;
  chatTitle: string;
  chatStatus: string;
  welcome: string;
  prompt: string;
  placeholder: string;
  send: string;
  benefitsTitle: string;
  safetyTitle: string;
  safetyText: string;
  emergencyTitle: string;
  emergencyText: string;
  clinicalNote: string;
  assistantReply: string;
  emergencyReply: string;
  questions: string[];
  benefits: {
    icon: typeof MessageCircle;
    title: string;
    text: string;
  }[];
};
const content: Record<'en' | 'ua' | 'ru' | 'ro', LocalizedContent> = {
  en: {
    eyebrow: 'Dentico Patient Assistant',
    title: 'Instant Dental Support, 24/7',
    subtitle: 'Our AI patient assistant can help you choose the right service, collect the key details about your dental concern, and guide you to the next available appointment.',
    startChat: 'Start AI Chat',
    whatsapp: 'WhatsApp Us',
    book: 'Book Online',
    trustedLabel: 'Designed to support, not replace, your dental team',
    chatTitle: 'Dentico Patient Assistant',
    chatStatus: 'Here to help you prepare for your visit',
    welcome: 'Hello — I can help you prepare for a visit, find the right type of appointment, or connect you with the clinic.',
    prompt: 'Choose a question or tell us what you need help with.',
    placeholder: 'Describe your concern…',
    send: 'Send message',
    benefitsTitle: 'A more helpful first step',
    safetyTitle: 'Safe and responsible support',
    safetyText: 'The assistant does not diagnose or replace a dentist. It helps collect the right information so your clinical consultation can start with more clarity.',
    emergencyTitle: 'For urgent medical emergencies',
    emergencyText: 'This AI assistant does not provide a diagnosis or emergency medical advice. If you have severe swelling, uncontrolled bleeding, difficulty breathing, facial trauma or a serious medical emergency, please call 999 or NHS 111 immediately.',
    clinicalNote: 'A clinician will assess your symptoms and recommend treatment after a proper consultation.',
    assistantReply: 'Thank you for sharing that. I can help route your enquiry, but a dentist will need to assess your symptoms. Would you like to book online or continue with the clinic on WhatsApp?',
    emergencyReply: 'Your symptoms may need urgent medical attention. Please call 999 or NHS 111 immediately. Do not wait for an online reply.',
    questions: ['I have tooth pain. What should I do?', 'Can I book an emergency appointment?', 'I want to continue my braces treatment.', 'How much does hygiene cost?', 'Do you speak Ukrainian or Russian?'],
    benefits: [{
      icon: MessageCircle,
      title: 'Quick guidance',
      text: 'Helps you understand which dental service may be suitable for your concern.'
    }, {
      icon: ClipboardList,
      title: 'Smart information collection',
      text: 'Collects helpful details such as symptoms, pain level, treatment history and preferred time.'
    }, {
      icon: Languages,
      title: 'Multilingual support',
      text: 'Supports English, Ukrainian, Russian and Romanian-speaking patients.'
    }, {
      icon: Calendar,
      title: 'Easy booking',
      text: 'Guides you to book online, call the clinic or continue via WhatsApp.'
    }, {
      icon: ShieldCheck,
      title: 'Safe and responsible',
      text: 'Keeps the focus on preparation and a proper clinical consultation — never a diagnosis.'
    }]
  },
  ua: {
    eyebrow: 'Асистент для пацієнтів Dentico',
    title: 'Миттєва стоматологічна підтримка 24/7',
    subtitle: 'Наш AI-асистент допоможе обрати потрібну послугу, зібрати ключові деталі щодо вашого звернення та перейти до найближчого доступного запису.',
    startChat: 'Почати AI-чат',
    whatsapp: 'Написати в WhatsApp',
    book: 'Записатися онлайн',
    trustedLabel: 'Підтримка перед візитом — не заміна стоматолога',
    chatTitle: 'Асистент для пацієнтів Dentico',
    chatStatus: 'Допомагає підготуватися до візиту',
    welcome: 'Вітаємо — я допоможу підготуватися до візиту, знайти відповідний тип запису або зв’язатися з клінікою.',
    prompt: 'Оберіть запитання або опишіть, з чим вам потрібна допомога.',
    placeholder: 'Опишіть ваше звернення…',
    send: 'Надіслати повідомлення',
    benefitsTitle: 'Зручний перший крок',
    safetyTitle: 'Безпечна та відповідальна підтримка',
    safetyText: 'Асистент не встановлює діагноз і не замінює стоматолога. Він допомагає зібрати потрібну інформацію, щоб консультація пройшла зрозуміліше.',
    emergencyTitle: 'У разі невідкладного стану',
    emergencyText: 'Цей AI-асистент не надає діагнозів або екстрених медичних порад. У разі сильного набряку, неконтрольованої кровотечі, утрудненого дихання, травми обличчя або серйозного невідкладного стану негайно телефонуйте 999 або NHS 111.',
    clinicalNote: 'Лікар оцінить ваші симптоми та порекомендує лікування після належної консультації.',
    assistantReply: 'Дякуємо за інформацію. Я можу допомогти спрямувати ваш запит, але симптоми має оцінити стоматолог. Бажаєте записатися онлайн чи продовжити спілкування з клінікою у WhatsApp?',
    emergencyReply: 'Ваші симптоми можуть потребувати невідкладної медичної допомоги. Негайно телефонуйте 999 або NHS 111. Не очікуйте відповіді онлайн.',
    questions: ['У мене болить зуб. Що робити?', 'Чи можу я записатися на терміновий прийом?', 'Я хочу продовжити ортодонтичне лікування.', 'Скільки коштує гігієна?', 'Ви розмовляєте українською або російською?'],
    benefits: [{
      icon: MessageCircle,
      title: 'Швидке спрямування',
      text: 'Допомагає зрозуміти, яка стоматологічна послуга може вам підійти.'
    }, {
      icon: ClipboardList,
      title: 'Розумний збір інформації',
      text: 'Збирає дані про симптоми, рівень болю, історію лікування та бажаний час.'
    }, {
      icon: Languages,
      title: 'Підтримка кількома мовами',
      text: 'Підтримує пацієнтів англійською, українською, російською та румунською.'
    }, {
      icon: Calendar,
      title: 'Простий запис',
      text: 'Спрямовує до онлайн-запису, дзвінка в клініку або WhatsApp.'
    }, {
      icon: ShieldCheck,
      title: 'Безпечно та відповідально',
      text: 'Допомагає підготуватися до консультації, але не встановлює діагноз.'
    }]
  },
  ru: {
    eyebrow: 'Ассистент для пациентов Dentico',
    title: 'Мгновенная стоматологическая поддержка 24/7',
    subtitle: 'Наш AI-ассистент поможет выбрать подходящую услугу, собрать ключевые детали о вашей проблеме и перейти к ближайшей доступной записи.',
    startChat: 'Начать AI-чат',
    whatsapp: 'Написать в WhatsApp',
    book: 'Записаться онлайн',
    trustedLabel: 'Поддержка перед визитом — не замена стоматолога',
    chatTitle: 'Ассистент для пациентов Dentico',
    chatStatus: 'Поможет подготовиться к визиту',
    welcome: 'Здравствуйте — я помогу подготовиться к визиту, выбрать подходящий тип записи или связаться с клиникой.',
    prompt: 'Выберите вопрос или расскажите, какая помощь вам нужна.',
    placeholder: 'Опишите ваш вопрос…',
    send: 'Отправить сообщение',
    benefitsTitle: 'Более удобный первый шаг',
    safetyTitle: 'Безопасная и ответственная поддержка',
    safetyText: 'Ассистент не ставит диагноз и не заменяет стоматолога. Он помогает собрать нужную информацию, чтобы клиническая консультация прошла понятнее.',
    emergencyTitle: 'При неотложном состоянии',
    emergencyText: 'Этот AI-ассистент не предоставляет диагнозов или неотложных медицинских рекомендаций. При сильном отёке, неконтролируемом кровотечении, затруднённом дыхании, травме лица или серьёзном медицинском состоянии немедленно звоните 999 или NHS 111.',
    clinicalNote: 'Стоматолог оценит симптомы и порекомендует лечение после полноценной консультации.',
    assistantReply: 'Спасибо за информацию. Я могу помочь направить ваш запрос, но симптомы должен оценить стоматолог. Хотите записаться онлайн или продолжить разговор с клиникой в WhatsApp?',
    emergencyReply: 'Ваши симптомы могут требовать срочной медицинской помощи. Немедленно позвоните 999 или NHS 111. Не ждите ответа онлайн.',
    questions: ['У меня болит зуб. Что делать?', 'Можно записаться на срочный приём?', 'Я хочу продолжить лечение брекетами.', 'Сколько стоит гигиена?', 'Вы говорите по-украински или по-русски?'],
    benefits: [{
      icon: MessageCircle,
      title: 'Быстрое направление',
      text: 'Помогает понять, какая стоматологическая услуга может подойти в вашей ситуации.'
    }, {
      icon: ClipboardList,
      title: 'Умный сбор информации',
      text: 'Собирает данные о симптомах, уровне боли, истории лечения и предпочтительном времени.'
    }, {
      icon: Languages,
      title: 'Поддержка на разных языках',
      text: 'Поддерживает пациентов на английском, украинском, русском и румынском языках.'
    }, {
      icon: Calendar,
      title: 'Простая запись',
      text: 'Направляет к онлайн-записи, звонку в клинику или WhatsApp.'
    }, {
      icon: ShieldCheck,
      title: 'Безопасно и ответственно',
      text: 'Помогает подготовиться к консультации, но не ставит диагноз.'
    }]
  },
  ro: {
    eyebrow: 'Asistentul pentru pacienți Dentico',
    title: 'Asistență dentară instantă, 24/7',
    subtitle: 'Asistentul nostru AI vă poate ajuta să alegeți serviciul potrivit, să colectați detaliile esențiale despre problema dentară și să găsiți următoarea programare disponibilă.',
    startChat: 'Începe chatul AI',
    whatsapp: 'WhatsApp',
    book: 'Programare online',
    trustedLabel: 'Sprijin înainte de vizită — nu înlocuiește echipa dentară',
    chatTitle: 'Asistentul pentru pacienți Dentico',
    chatStatus: 'Vă ajută să vă pregătiți pentru vizită',
    welcome: 'Bun venit — vă pot ajuta să vă pregătiți pentru o vizită, să găsiți tipul potrivit de programare sau să luați legătura cu clinica.',
    prompt: 'Alegeți o întrebare sau spuneți-ne cu ce aveți nevoie de ajutor.',
    placeholder: 'Descrieți problema dumneavoastră…',
    send: 'Trimite mesajul',
    benefitsTitle: 'Un prim pas mai util',
    safetyTitle: 'Sprijin sigur și responsabil',
    safetyText: 'Asistentul nu pune diagnostice și nu înlocuiește un dentist. Vă ajută să colectați informațiile potrivite pentru o consultație mai clară.',
    emergencyTitle: 'Pentru urgențe medicale',
    emergencyText: 'Acest asistent AI nu oferă diagnostice sau sfaturi medicale de urgență. Dacă aveți umflături severe, sângerare necontrolată, dificultăți de respirație, traumatisme faciale sau o urgență medicală gravă, sunați imediat la 999 sau NHS 111.',
    clinicalNote: 'Un clinician vă va evalua simptomele și va recomanda tratamentul după o consultație corespunzătoare.',
    assistantReply: 'Vă mulțumim pentru informații. Vă pot ajuta să direcționați solicitarea, dar simptomele trebuie evaluate de un dentist. Doriți să programați online sau să continuați cu clinica pe WhatsApp?',
    emergencyReply: 'Simptomele dumneavoastră pot necesita asistență medicală urgentă. Sunați imediat la 999 sau NHS 111. Nu așteptați un răspuns online.',
    questions: ['Am durere de dinți. Ce ar trebui să fac?', 'Pot programa o consultație de urgență?', 'Vreau să continui tratamentul cu aparat dentar.', 'Cât costă igiena dentară?', 'Vorbiți ucraineană sau rusă?'],
    benefits: [{
      icon: MessageCircle,
      title: 'Îndrumare rapidă',
      text: 'Vă ajută să înțelegeți ce serviciu dentar poate fi potrivit pentru problema dumneavoastră.'
    }, {
      icon: ClipboardList,
      title: 'Colectare inteligentă de informații',
      text: 'Colectează date despre simptome, nivelul durerii, istoricul tratamentelor și ora preferată.'
    }, {
      icon: Languages,
      title: 'Suport multilingv',
      text: 'Sprijină pacienții vorbitori de engleză, ucraineană, rusă și română.'
    }, {
      icon: Calendar,
      title: 'Programare simplă',
      text: 'Vă ghidează spre programare online, apel la clinică sau WhatsApp.'
    }, {
      icon: ShieldCheck,
      title: 'Sigur și responsabil',
      text: 'Vă ajută să vă pregătiți pentru consultație, fără a pune un diagnostic.'
    }]
  }
};
export function AiSupportPage() {
  const {
    lang
  } = useTranslation();
  const locale = lang as 'en' | 'ua' | 'ru' | 'ro';
  const copy = content[locale];
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([{
    id: 1,
    role: 'assistant',
    text: copy.welcome
  }]);
  const focusChat = () => {
    chatRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    window.setTimeout(() => inputRef.current?.focus(), 350);
  };
  const sendMessage = (text = input) => {
    const cleanText = text.trim();
    if (!cleanText) return;
    const needsUrgentCare = /severe swelling|uncontrolled bleeding|difficulty breathing|facial trauma|сильн.*набряк|неконтрольован.*кровотеч|утруднен.*дихан|тяжел.*отек|неконтролируем.*кровотеч|затруднен.*дыхан|umflături severe|sângerare necontrolată|dificultăți de respirație|traumatisme faciale/i.test(cleanText);
    setMessages(current => [...current, {
      id: Date.now(),
      role: 'patient',
      text: cleanText
    }, {
      id: Date.now() + 1,
      role: 'assistant',
      text: needsUrgentCare ? copy.emergencyReply : copy.assistantReply
    }]);
    setInput('');
  };
  const seoTitle = lang === 'en' ? 'AI Patient Support | Dentico Clinic' : lang === 'ua' ? 'AI-підтримка пацієнтів | Dentico Clinic' : lang === 'ru' ? 'AI-поддержка пациентов | Dentico Clinic' : 'Asistență AI pentru Pacienți | Dentico Clinic';
  const seoDescription = lang === 'en' ? 'Get quick, safe guidance before booking with the Dentico Clinic AI patient assistant.' : lang === 'ua' ? 'Отримайте швидке та безпечне спрямування перед записом з AI-асистентом Dentico Clinic.' : lang === 'ru' ? 'Получите быстрое и безопасное направление перед записью с AI-ассистентом Dentico Clinic.' : 'Obțineți îndrumare rapidă și sigură înainte de programare cu asistentul AI Dentico Clinic.';
  return <Layout title={seoTitle} description={seoDescription} data-id="element-694" data-mp="2yk86fin6dpn">
      <section className="bg-ivory border-b border-beige/60 overflow-hidden" data-id="element-695" data-mp="3qkux3x9e9vs8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-id="element-696" data-mp="3mkb0qvwnaw75">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-20 items-center" data-id="element-697" data-mp="3mmdnn72i497n">
            <div data-id="element-698" data-mp="4c23fn2r726s">
              <div className="inline-flex items-center gap-2 rounded-full bg-forest-50 border border-forest-100 px-3.5 py-2 text-sm font-medium text-forest-700" data-id="element-699" data-mp="3vkz4mxsbrg6b">
                <Bot className="w-4 h-4" strokeWidth={1.8} data-id="element-700" data-mp="37veiy74se4tc" />
                {copy.eyebrow}
              </div>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-medium tracking-tight text-forest-900 leading-[1.06]" data-id="element-701" data-mp="32tz10nxwoef6">
                {copy.title}
              </h1>
              <p className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-charcoal-light" data-id="element-702" data-mp="3cswxcd1kpc8q">
                {copy.subtitle}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-703" data-mp="2k62fzp01yszh">
                <Button size="lg" onClick={focusChat} className="w-full sm:w-auto" data-id="element-704" data-mp="o2fhsisnmg3a">
                  <Bot className="w-5 h-5" data-id="element-705" data-mp="o0l7rzix88dz" />
                  {copy.startChat}
                </Button>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-706" data-mp="sz5yn8ca9fpr">
                  <Button size="lg" variant="secondary" fullWidth data-id="element-707" data-mp="11tkmw0esk0l0">
                    <MessageCircle className="w-5 h-5" data-id="element-708" data-mp="11t17aqcm5oa5" />
                    {copy.whatsapp}
                  </Button>
                </a>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-709" data-mp="a2d7bhmoeoy2">
                  <Button size="lg" variant="ghost" fullWidth className="border border-beige/80" data-id="element-710" data-mp="e3yovga5nrel">
                    <Calendar className="w-5 h-5" data-id="element-711" data-mp="j3l23ppzh7ne" />
                    {copy.book}
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex items-center gap-2 text-sm text-stone-600" data-id="element-712" data-mp="1pj53icnh2lr4">
                <CheckCircle2 className="w-4 h-4 text-forest-600 flex-shrink-0" data-id="element-713" data-mp="1ugwyxgnbsq02" />
                {copy.trustedLabel}
              </div>
            </div>

            <div ref={chatRef} className="rounded-[28px] border border-beige/70 bg-ivory-50 shadow-[0_20px_50px_rgba(47,74,63,0.14)] overflow-hidden" aria-label={copy.chatTitle} data-id="element-714" data-mp="1zmtpbzn06d6r">
              <div className="bg-forest-800 px-5 sm:px-6 py-4 flex items-center gap-3 text-white" data-id="element-715" data-mp="1qu8kzok4c91g">
                <div className="w-10 h-10 rounded-full bg-ivory/15 border border-white/20 flex items-center justify-center" data-id="element-716" data-mp="1lwqc1y63pttg">
                  <Bot className="w-5 h-5 text-beige" strokeWidth={1.7} data-id="element-717" data-mp="1gyx6968pn1tp" />
                </div>
                <div data-id="element-718" data-mp="1bztxbapm0bgg">
                  <p className="font-semibold leading-tight" data-id="element-719" data-mp="17w5rawgmlnxb">{copy.chatTitle}</p>
                  <p className="text-xs text-ivory/70 mt-0.5" data-id="element-720" data-mp="17y8s8bjbh1gt">{copy.chatStatus}</p>
                </div>
                <span className="ml-auto flex items-center gap-1.5 text-xs text-ivory/80" data-id="element-721" data-mp="369ft9ct3vl5m">
                  <span className="w-2 h-2 rounded-full bg-beige" aria-hidden="true" data-id="element-722" data-mp="3282katupv1uh" />
                  24/7
                </span>
              </div>

              <div className="p-4 sm:p-6 bg-ivory-50" data-id="element-723" data-mp="3pks59j8bbhry">
                <div className="space-y-3 max-h-[270px] overflow-y-auto pr-1" aria-live="polite" data-id="element-724" data-mp="3pmcvwxriagkj">
                  {messages.map(message => <div key={message.id} className={`flex ${message.role === 'patient' ? 'justify-end' : 'justify-start'}`} data-id="element-725" data-mp="3gr6oxvgi1sgf">
                      <p className={`max-w-[86%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${message.role === 'patient' ? 'rounded-br-md bg-forest-700 text-white' : 'rounded-bl-md bg-white border border-beige/60 text-charcoal shadow-sm'}`} data-id="element-726" data-mp="31tmmav72s9df">
                        {message.text}
                      </p>
                    </div>)}
                </div>

                <p className="mt-5 text-xs font-medium text-stone-600" data-id="element-727" data-mp="r47o6crm8d4n">{copy.prompt}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-728" data-mp="w293qnpu1ubd">
                  {copy.questions.map(question => <button key={question} type="button" onClick={() => sendMessage(question)} className="rounded-full border border-beige bg-white px-3 py-2 text-left text-xs text-forest-700 hover:bg-forest-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beige" data-id="element-729" data-mp="166pmboup5piv">
                      {question}
                    </button>)}
                </div>

                <form className="mt-5 flex gap-2 rounded-xl border border-beige bg-white p-1.5" onSubmit={event => {
                event.preventDefault();
                sendMessage();
              }} data-id="element-730" data-mp="1jwja03ugu3r0">
                  <label className="sr-only" htmlFor="ai-support-message" data-id="element-731" data-mp="3iayr5f5bguo">{copy.placeholder}</label>
                  <input ref={inputRef} id="ai-support-message" value={input} onChange={event => setInput(event.target.value)} className="min-w-0 flex-1 bg-transparent px-3 text-sm text-charcoal placeholder:text-stone-500 focus:outline-none" placeholder={copy.placeholder} data-id="element-732" data-mp="3vnn88r99cklk" />
                  <button type="submit" aria-label={copy.send} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-forest-600 text-white hover:bg-forest-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beige" data-id="element-733" data-mp="37soha3vmcx27">
                    <Send className="w-4 h-4" data-id="element-734" data-mp="2t0mo12o212b5" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-id="element-735" data-mp="8ax8enfj1che">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-736" data-mp="4b6i0ktwwyde">
          <div className="max-w-2xl mb-10 lg:mb-14" data-id="element-737" data-mp="4czgvdyaaeau">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-forest-600" data-id="element-738" data-mp="bte7dk3fd08l">{copy.eyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-medium text-forest-900" data-id="element-739" data-mp="guaavwjv6kdh">{copy.benefitsTitle}</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-740" data-mp="1ycfighrlk2b">
            {copy.benefits.map(benefit => {
            const Icon = benefit.icon;
            return <article key={benefit.title} className="rounded-2xl border border-beige/60 bg-ivory p-6 sm:p-7" data-id="element-741" data-mp="ulp4exbee3ys">
                  <div className="w-11 h-11 rounded-xl bg-forest-50 border border-forest-100 flex items-center justify-center" data-id="element-742" data-mp="zm367xmeohod">
                    <Icon className="w-5 h-5 text-forest-700" strokeWidth={1.7} data-id="element-743" data-mp="ptx9kal7lc2u" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-forest-900" data-id="element-744" data-mp="1dj9hl74sve3k">{benefit.title}</h3>
                  <p className="mt-3 leading-relaxed text-charcoal-light" data-id="element-745" data-mp="1ib1ctuywi801">{benefit.text}</p>
                </article>;
          })}
          </div>
        </div>
      </section>

      <section className="bg-forest-900 py-16 lg:py-20 text-white" data-id="element-746" data-mp="1n7n7mzco5is9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-747" data-mp="1n5aqupindegs">
          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-8 lg:gap-14 items-start" data-id="element-748" data-mp="1j4qrrgeik0s0">
            <div data-id="element-749" data-mp="1e7i9zgxa1yra">
              <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center" data-id="element-750" data-mp="1e5xglump8lv0">
                <ShieldCheck className="w-6 h-6 text-beige" strokeWidth={1.7} data-id="element-751" data-mp="199p6ivncziup" />
              </div>
              <h2 className="mt-5 text-3xl sm:text-4xl font-display font-medium" data-id="element-752" data-mp="14et1mux0x106">{copy.safetyTitle}</h2>
              <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ivory/80" data-id="element-753" data-mp="zfq8o8ubil8k">{copy.safetyText}</p>
              <p className="mt-5 flex gap-2 text-sm leading-relaxed text-ivory/70" data-id="element-754" data-mp="ve4qc7z0leqz">
                <Stethoscope className="w-4 h-4 flex-shrink-0 mt-0.5 text-beige" data-id="element-755" data-mp="qgcux3z5vai1" />
                {copy.clinicalNote}
              </p>
            </div>
            <aside className="rounded-2xl border border-beige/30 bg-white/10 p-6 sm:p-7" data-id="element-756" data-mp="2x5wsox0f6u8x">
              <div className="flex gap-3" data-id="element-757" data-mp="323yjk4437xuy">
                <TriangleAlert className="w-6 h-6 flex-shrink-0 text-beige" strokeWidth={1.8} data-id="element-758" data-mp="322wwf19srkwv" />
                <div data-id="element-759" data-mp="3742r34n0pi91">
                  <h3 className="text-lg font-semibold" data-id="element-760" data-mp="371gh6ycnmqnn">{copy.emergencyTitle}</h3>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-ivory/85" data-id="element-761" data-mp="3b4d77a4ixp1m">{copy.emergencyText}</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-ivory-200 py-14 lg:py-16" data-id="element-762" data-mp="2jbw0yn7vy7i2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-763" data-mp="2neiucsk7g0hd">
          <Clock3 className="w-7 h-7 mx-auto text-forest-700" strokeWidth={1.6} data-id="element-764" data-mp="2s6twm22f3yd3" />
          <h2 className="mt-4 text-3xl font-display font-medium text-forest-900" data-id="element-765" data-mp="1voe267q7e95l">{copy.title}</h2>
          <p className="mt-3 text-charcoal-light" data-id="element-766" data-mp="1zpzmge52hppt">{copy.subtitle}</p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3" data-id="element-767" data-mp="1zo6gz266xbje">
            <Button size="lg" onClick={focusChat} className="w-full sm:w-auto" data-id="element-768" data-mp="1gm5u0j8633x2">
              <Bot className="w-5 h-5" data-id="element-769" data-mp="1bnci2v8j3kz0" />
              {copy.startChat}
            </Button>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-770" data-mp="1qbfou6ueb0ov">
              <Button size="lg" variant="secondary" fullWidth data-id="element-771" data-mp="1ljfvix8tz619">
                {copy.book}
                <ArrowRight className="w-5 h-5" data-id="element-772" data-mp="1zb2qmtr1e8ig" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}